import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, UserPlus, Mail, User, Loader2, AlertCircle } from 'lucide-react';
import api from '../../../../api/client';

const InviteAdminModal = ({ isOpen, onClose, onInvite, companyName }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);

  if (!isOpen) return null;

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = t('companyAdmins.inviteModalFirstNameRequired');
    if (!formData.lastName.trim()) newErrors.lastName = t('companyAdmins.inviteModalLastNameRequired');
    if (!formData.email.trim()) {
      newErrors.email = t('companyAdmins.inviteModalEmailRequired');
    } else if (!validateEmail(formData.email)) {
      newErrors.email = t('companyAdmins.inviteModalInvalidEmail');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    setApiError(null);
    
    try {
      const response = await api.post('/v1/admin/invite/', formData);
      
      // Success case
      onInvite(formData.email, response.data?.message);
      handleClose();
    } catch (err) {
      console.error('Error sending invitation:', err);
      
      let errorMessage = t('companyAdmins.inviteModalFailedToSend');
      
      if (err.response) {
        if (err.response.status === 403) {
          errorMessage = t('companyAdmins.inviteModalForbidden');
        } else if (err.response.status === 400) {
          errorMessage = err.response.data?.error || t('companyAdmins.inviteModalUserExists');
        } else {
          errorMessage = err.response.data?.error || err.response.data?.detail || errorMessage;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setApiError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({ firstName: '', lastName: '', email: '' });
    setErrors({});
    setApiError(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[999999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div 
          className="px-6 py-4 border-b border-gray-100 flex items-center justify-between"
          style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}
        >
          <div className="flex items-center gap-2 text-white">
            <UserPlus size={20} />
            <h2 className="text-xl text-white font-bold">{t('companyAdmins.inviteModalTitle')}</h2>
          </div>
          <button 
            onClick={handleClose}
            className="text-white/80 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {apiError && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 animate-in fade-in duration-200">
              <AlertCircle size={16} className="flex-shrink-0" />
              <span>{apiError}</span>
            </div>
          )}
          
          {companyName && (
            <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-sm text-blue-700">
                {t('companyAdmins.inviteModalInvitingTo')} <span className="font-bold">{companyName}</span>
              </p>
            </div>
          )}

          {/* First Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              {t('companyAdmins.inviteModalFirstName')}
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                className={`w-full pl-10 pr-4 py-2 border rounded-xl focus:ring-2 focus:ring-sky-500 outline-none transition-all ${
                  errors.firstName ? 'border-red-500 bg-red-50' : 'border-gray-200'
                }`}
                placeholder={t('companyAdmins.inviteModalPlaceholderFirstName')}
              />
            </div>
            {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
          </div>

          {/* Last Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              {t('companyAdmins.inviteModalLastName')}
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                className={`w-full pl-10 pr-4 py-2 border rounded-xl focus:ring-2 focus:ring-sky-500 outline-none transition-all ${
                  errors.lastName ? 'border-red-500 bg-red-50' : 'border-gray-200'
                }`}
                placeholder={t('companyAdmins.inviteModalPlaceholderLastName')}
              />
            </div>
            {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              {t('companyAdmins.inviteModalEmailAddress')}
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className={`w-full pl-10 pr-4 py-2 border rounded-xl focus:ring-2 focus:ring-sky-500 outline-none transition-all ${
                  errors.email ? 'border-red-500 bg-red-50' : 'border-gray-200'
                }`}
                placeholder={t('companyAdmins.inviteModalPlaceholderEmail')}
              />
            </div>
            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-600 font-semibold rounded-xl hover:bg-gray-50 transition-colors"
            >
              {t('companyAdmins.inviteModalCancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2.5 text-white font-semibold rounded-xl hover:shadow-lg hover:opacity-90 transition-all disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  {t('companyAdmins.inviteModalSending')}
                </>
              ) : (
                t('companyAdmins.inviteModalSendInvitation')
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InviteAdminModal;
